<script lang="ts">
	import type { SbImage, SbLink, SbVideo } from '../types';
	import BtnPlayVideo from '$lib/buttons/v2/btnPlayVideo.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import Button from '$lib/buttons/v3/button.svelte';
	import { slideIn } from '$lib/actions/slideIn';
	import Image from '../components/image.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import { device } from '$lib/state/device.svelte';

	interface Props {
		content: ISbStoryData<{
			asset: Array<ISbStoryData['content']>;
			asset_desktop: Array<ISbStoryData['content']>;
			overlay: boolean;
			headline_image: SbImage;
			text: string;
			cta_text?: string;
			cta_url: SbLink;
			cta_color: 'green-3' | 'green-4' | 'green-5' | 'white';
			show_circle_outline: boolean;
			fullscreen_video: Array<ISbStoryData<SbVideo>['content']>;
		}>['content'];
	}

	let { content }: Props = $props();

	function getBtnTheme(color: typeof content.cta_color): string | undefined {
		switch (color) {
			case 'white':
				return 'btn-stone';
			case 'green-5':
				return 'btn-lime';
			case 'green-4':
				return 'btn-dark-lime';
			case 'green-3':
			default:
				return undefined;
		}
	}

	let btnTheme = $derived(getBtnTheme(content.cta_color));

	let asset = $derived(
		device.data.isDesktop ? content.asset_desktop?.[0] || content.asset?.[0] : content.asset?.[0]
	);

	let fullscreenVideo = $derived(content.fullscreen_video?.[0]);
</script>

<section
	use:editable={content}
	id="hero"
	data-testid="hero-section"
	class="relative flex w-full flex-col justify-center"
>
	{#if asset}
		<div class="absolute inset-0"><Blok content={asset} /></div>
	{/if}

	{#if content.overlay}
		<div class="pointer-events-none absolute inset-0 bg-black/40"></div>
	{/if}

	<div
		class="flex h-full flex-col items-center justify-end max-lg:mb-8 max-lg:px-5 lg:justify-center"
	>
		<div
			class="flex flex-col items-center justify-center gap-y-4"
			class:aspect-square={content.show_circle_outline}
			class:max-w-[90%]={content.show_circle_outline}
			class:lg:max-w-[464px]={content.show_circle_outline}
			class:border-2={content.show_circle_outline}
			class:border-white={content.show_circle_outline}
			class:rounded-full={content.show_circle_outline}
		>
			{#if content.headline_image?.filename}
				<div
					use:slideIn={{ duration: 480 }}
					class="z-10 w-full max-w-[700px] select-none opacity-[0.01]"
				>
					<Image
						src={content.headline_image.filename}
						class="h-full w-full object-contain"
						alt={content.headline_image?.alt}
						title={content.headline_image?.title}
						width={700}
						height={700}
					/>
				</div>
			{/if}
		</div>

		{#if content.text}
			<p
				use:slideIn={{ duration: 480, delay: 80 }}
				class="z-10 mx-10 mb-9 mt-5 max-w-[940px] text-center text-[20px]/[140%] text-white opacity-[0.01] lg:mb-[34px] lg:mt-8 lg:text-balance lg:text-[28px]/[113%]"
			>
				{content.text}
			</p>
		{/if}

		{#if fullscreenVideo || (content.cta_text && content.cta_text.length > 0)}
			<div
				use:slideIn={{ duration: 480, delay: 120 }}
				class="flex w-full justify-center gap-x-3 opacity-[0.01]"
			>
				{#if content.cta_text && content.cta_text.length > 0}
					<Button
						class={[btnTheme, 'btn-large'].filter(Boolean).join(' ')}
						href={getSbUrl(content.cta_url)}>{content.cta_text}</Button
					>
				{/if}
				{#if fullscreenVideo}
					<BtnPlayVideo playbackId={fullscreenVideo.playback_id} />
				{/if}
			</div>
		{/if}
	</div>
</section>

<style lang="scss">
	section {
		height: calc(100svh - var(--banner-height));
		transition-property: padding-top, height;
		transition-duration: 0.2s;
		padding-top: var(--banner-height);
	}
</style>
